import { CircleMarker } from "../../../types/CircleMarker";
import { ApiRequestError } from "../../../utils/api";

export type HitDetectionStatus = "ACCEPTED" | "REVIEW";

export interface HitsScannerV2Request {
  image: string;
  targetId: string;
  centerFieldCenter: {
    x: number;
    y: number;
  };
  centerFieldRadius: number;
  expectedHitRadius: number;
}

export interface HitsScannerV2Response {
  modelVersion: string;
  detections: Array<{
    center: {
      x: number;
      y: number;
    };
    confidence: number;
    status: HitDetectionStatus;
  }>;
}

export interface DetectedHitMarker extends CircleMarker {
  newlyManuallyAdded: boolean;
  detectionStatus: HitDetectionStatus;
  confidence: number;
}

interface BuildHitsScannerV2RequestParams {
  image: string;
  targetId: string;
  renderedCenterField: CircleMarker;
  markersDrawingScaleFactor: number;
  targetCenterFieldRadius: number;
  hitRadius: number;
}

export class HitsScannerValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "HitsScannerValidationError";
  }
}

const isFiniteNonNegative = (value: number): boolean => Number.isFinite(value) && value >= 0;
const isFinitePositive = (value: number): boolean => Number.isFinite(value) && value > 0;

export const buildHitsScannerV2Request = ({
  image,
  targetId,
  renderedCenterField,
  markersDrawingScaleFactor,
  targetCenterFieldRadius,
  hitRadius
}: BuildHitsScannerV2RequestParams): HitsScannerV2Request => {
  const dataSeparatorIndex = image.indexOf(",");
  const hasImageData = image.startsWith("data:image/")
    && image.slice(0, dataSeparatorIndex).endsWith(";base64")
    && dataSeparatorIndex > 0
    && dataSeparatorIndex < image.length - 1;

  if (!hasImageData) {
    throw new HitsScannerValidationError("Obraz musi być zapisany jako dane base64.");
  }
  if (!targetId.trim()) {
    throw new HitsScannerValidationError("Nie wybrano typu tarczy.");
  }
  if (!isFiniteNonNegative(renderedCenterField.x) || !isFiniteNonNegative(renderedCenterField.y)) {
    throw new HitsScannerValidationError("Środek czarnego pola ma nieprawidłowe współrzędne.");
  }
  if (!isFinitePositive(renderedCenterField.radius)
    || !isFinitePositive(markersDrawingScaleFactor)
    || !isFinitePositive(targetCenterFieldRadius)
    || !isFinitePositive(hitRadius)) {
    throw new HitsScannerValidationError("Promienie używane do wykrywania trafień muszą być dodatnie.");
  }

  const centerFieldRadius = renderedCenterField.radius / markersDrawingScaleFactor;

  return {
    image,
    targetId,
    centerFieldCenter: {
      x: renderedCenterField.x / markersDrawingScaleFactor,
      y: renderedCenterField.y / markersDrawingScaleFactor
    },
    centerFieldRadius,
    expectedHitRadius: centerFieldRadius / targetCenterFieldRadius * hitRadius
  };
};

export const mapHitDetectionsToMarkers = (
  response: HitsScannerV2Response,
  markersDrawingScaleFactor: number,
  hitMarkerRadius: number
): DetectedHitMarker[] => response.detections.map((detection) => ({
  x: detection.center.x * markersDrawingScaleFactor,
  y: detection.center.y * markersDrawingScaleFactor,
  radius: hitMarkerRadius,
  newlyManuallyAdded: false,
  detectionStatus: detection.status,
  confidence: detection.confidence
}));

export const getHitMarkerStatusClassName = (status?: HitDetectionStatus): string => {
  if (status === "ACCEPTED") {
    return "hit-marker--accepted";
  }
  if (status === "REVIEW") {
    return "hit-marker--review";
  }
  return "hit-marker--manual";
};

export const getHitsScannerErrorMessage = (error: unknown): string => {
  if (error instanceof HitsScannerValidationError) {
    return error.message;
  }

  if (error instanceof ApiRequestError) {
    if (error.status === 422 || error.code === "UNSUPPORTED_TARGET") {
      return "Automatyczne wykrywanie trafień nie obsługuje wybranego typu tarczy. Dodaj oznaczenia ręcznie.";
    }
    if (error.status === 503 || error.code === "HIT_DETECTOR_UNAVAILABLE") {
      return "Automatyczne wykrywanie trafień jest chwilowo niedostępne. Dodaj oznaczenia ręcznie.";
    }
    if (error.status === 400 || error.code === "BAD_REQUEST") {
      return "Dane skanowania są nieprawidłowe. Sprawdź obraz oraz oznaczenie czarnego pola i spróbuj ponownie.";
    }

    return error.message;
  }

  if (error instanceof Error && error.message === "Failed to fetch") {
    return "Nie udało się połączyć z serwerem. Dodaj oznaczenia ręcznie lub spróbuj ponownie później.";
  }

  return "Nie udało się automatycznie wykryć trafień. Dodaj oznaczenia ręcznie.";
};
